/**
 * Support — understand customer questions, find relevant knowledge and
 * tickets, propose governed answers, and escalate to engineering explicitly.
 *
 * Built on shipped functionality: intake dispatcher + triage
 * (src/server/intake-agent/), grounded KB answering (support/answer.ts),
 * ticket-question answering (src/server/tickets/), the governed reply
 * pipeline (comm-governance + outbound_email_drafts), and the support-desk
 * behavior of migration 169 (no auto-remediation; escalation is explicit).
 */
import type { UseCaseDefinition } from "../types";

export const supportUseCase: UseCaseDefinition = {
  id: "support",
  version: 1,
  labelKey: "useCases.support.label",
  shortDescriptionKey: "useCases.support.short",
  fullDescriptionKey: "useCases.support.full",
  primaryOutcomeKey: "useCases.support.outcome",
  availability: "available",
  applicableWorkProfiles: ["support", "manager_reviewer", "admin", "custom"],
  requiredCapabilities: ["intake.receive", "knowledge.answer"],
  recommendedCapabilities: [
    "tickets.read",
    "reply.govern",
    "reply.send",
    "approvals.route",
    "notifications.post",
  ],
  suggestedBindingPurposes: ["intake", "status", "notifications", "approvals"],
  defaultExecutionMode: "read_only",
  defaultRemediationStartMode: "manual",
  onboardingStepIds: [
    "support.process-test-question",
    "support.look-up-ticket",
    "support.draft-answer",
  ],
  dashboardSectionIds: [
    "support.questions",
    "support.tickets",
    "shared.approvals",
    "shared.knowledge",
    "shared.activity",
  ],
  readinessCheckIds: [
    "support.intake-source",
    "support.knowledge-source",
    "support.reply-destination",
    "support.approval-policy",
    "support.send-health",
  ],
  defaultGovernanceProfileId: "balanced",
  prerequisiteDescriptionKeys: [
    "useCases.support.prereq.intake",
    "useCases.support.prereq.knowledge",
  ],
};
